import store from 'redux/store';
import { deleteRecurrenceDefinition } from 'redux/slices/recurrenceDefinitionsSlice';
import { addToTransportExecutor } from 'actions/factories/addToTransportExecutor';
import { deleteExecutor } from 'actions/factories/deleteExecutor';
import type { ActionDefinitions, ActionResult } from 'actions/Action';
import type { ActionEntity } from 'actions/ActionEntity';
import { showEditRecurrenceDefinition } from './edit/showEditRecurrenceDefinition';

type RecurrenceEntity = Extract<ActionEntity, { type: 'recurrence_definition' }>;

// Type guard over the whole selection: true (and narrows) when every entity is
// a recurrence definition — vacuously so for an empty selection. So `'hidden'`
// means only "wrong type"; an empty selection falls through to the count checks
// and shows contextual actions disabled, like a multi-selection does.
const isAllRecurrence = (entities: readonly ActionEntity[]): entities is readonly RecurrenceEntity[] =>
  entities.every((entity) => entity.type === 'recurrence_definition');

/**
 * Action definitions for recurrence definitions — the reference adoption of the
 * registry.
 *
 * `createRecurrenceDefinition` and `addToTransportRecurrenceDefinition` are
 * scoped: always present (transport disabled until a selection exists) and
 * fully-qualified so they never collide with other types' scoped actions.
 * `edit` / `duplicate` (single-only) and `delete` are contextual: plain names,
 * `'hidden'` only for a wrong-type selection and `'disabled'` for an empty or
 * (where single-only) multi selection. Edit-dialog actions refresh the host
 * list only on a successful save.
 */
export const recurrenceDefinitionActionDefinitions: ActionDefinitions = [
  {
    id: 'createRecurrenceDefinition',
    scope: 'type',
    label: 'New Recurrence',
    resolve: (ctx) => (): void => void showEditRecurrenceDefinition({ mode: 'create', createIn: ctx.createIn }).then((saved) => saved && ctx.refresh()),
  },
  {
    id: 'edit',
    label: 'Edit',
    resolve: (ctx): ActionResult => {
      const { entities, refresh } = ctx;
      if (!isAllRecurrence(entities)) return 'hidden';
      if (entities.length !== 1) return 'disabled';
      const [only] = entities;
      return () => void showEditRecurrenceDefinition({ mode: 'edit', zoneName: only.zoneName, name: only.name }).then((saved) => saved && refresh());
    },
  },
  {
    id: 'duplicate',
    label: 'Duplicate',
    resolve: (ctx): ActionResult => {
      const { entities, refresh } = ctx;
      if (!isAllRecurrence(entities)) return 'hidden';
      if (entities.length !== 1) return 'disabled';
      const [only] = entities;
      return () => void showEditRecurrenceDefinition({ mode: 'duplicate', zoneName: only.zoneName, name: only.name }).then((saved) => saved && refresh());
    },
  },
  {
    id: 'delete',
    label: 'Delete',
    resolve: (ctx): ActionResult => {
      const { entities, refresh } = ctx;
      if (!isAllRecurrence(entities)) return 'hidden';
      if (entities.length === 0) return 'disabled';
      return deleteExecutor(entities, (entity) => store.dispatch(deleteRecurrenceDefinition({ zone_name: entity.zoneName, name: entity.name })), {
        refresh,
        confirmBody: `Are you sure you want to delete ${entities.length === 1 ? 'this recurrence definition' : `these ${entities.length} recurrence definitions`}?`,
      });
    },
  },
  {
    id: 'addToTransportRecurrenceDefinition',
    label: 'Add to Transport',
    resolve: (ctx): ActionResult => {
      const { entities } = ctx;
      if (entities.length === 0) return 'disabled';
      if (!isAllRecurrence(entities)) return 'hidden';
      const single = entities.length === 1 ? entities[0] : null;
      return addToTransportExecutor(
        'recurrence_definition',
        entities.map((entity) => entity.id),
        { objectLabel: single ? `${single.zoneName} / ${single.name}` : 'Selected recurrence definitions' }
      );
    },
  },
];
